import { useField } from 'formik';
import React__default, { useCallback } from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { CHECKBOX_SIZE_REM } from '../inputs/InputCommon.js';
import { Box } from '../layout/Box.js';
import { Inline } from '../layout/Inline.js';
import { Stack } from '../layout/Stack.js';
import { getThemeVariantColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { onChangeFieldWrapper, hasError } from './Field.js';
import { setErrorDataAttribute } from './FieldError.js';

/**
 * Styled Components
 */ const CustomRadioInput = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 50%;
  height: ${CHECKBOX_SIZE_REM}rem;
  width: ${CHECKBOX_SIZE_REM}rem;
  background-color: transparent;
  border: 2px solid ${COLOR_PALETTE.slateGrey10};

  &:after {
    content: '';
    position: absolute;
    background-color: ${COLOR_PALETTE.white};
    border-radius: 50%;
    height: 0.75rem;
    width: 0.75rem;
    left: 0.125rem;
    top: 0.125rem;
  }
`;
const RadioLabel = styled.label`
  display: block;
  position: relative;
  cursor: pointer;
  user-select: none;
  font-weight: inherit;

  & input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  &:hover input ~ [data-hpy-web-radio-field-label] {
    /* TODO: a hover & focus state need to be re-added. removed for expediency in fixing issues
     but should be replaced with a newer design. Currently cannot use rating items on keyboard because of this. */
    /* border: 1px solid ${(props)=>props.color}; */
  }

  &:focus input ~ [data-hpy-web-radio-field-label],
  & input:focus ~ [data-hpy-web-radio-field-label] {
    /* TODO: a hover & focus state need to be re-added. removed for expediency in fixing issues
     but should be replaced with a newer design. Currently cannot use rating items on keyboard because of this. */
    /* border: 1px solid ${(props)=>props.color}; */
  }

  & input:checked ~ [data-hpy-web-radio-field-custom-input] {
    background-color: ${COLOR_PALETTE.white};
    border-color: ${getThemeVariantColor("context")};
  }

  & input:checked ~ [data-hpy-web-radio-field-custom-input]::after {
    background-color: ${getThemeVariantColor("context")};
  }

  & input:checked ~ [data-hpy-web-radio-field-label]:after {
    display: block;
  }
`;
const RadioInputWrapper = styled(Box)`
  height: ${CHECKBOX_SIZE_REM}rem;
  width: ${CHECKBOX_SIZE_REM}rem;
  position: absolute;
  /* provide the exact position on absolute so that cross-browser works fine */
  left: 0rem;
  top: 0rem;
`;
const LabelWrapper = styled(Box)`
  padding-left: 1.75rem;
  padding-top: 0.3rem;
`;
/**
 * Radio Input with Formik Field bindings.
 *
 * @example
 * <RadioField name="chekkkers" />
 */ const RadioField = ({ name , label , sublabel , value , onFieldChange , testId ="X" , ...props })=>{
    const [field, meta, helpers] = useField({
        name,
        type: "radio",
        value
    });
    const { setValue  } = helpers;
    // wrap the on change to perform side effects if needed
    const onChange = useCallback((e)=>{
        onFieldChange ? onChangeFieldWrapper(onFieldChange, setValue)(e.target.value) : setValue(e.target.value);
    }, [
        onFieldChange,
        setValue
    ]);
    const hasLabel = label || sublabel;
    return React__default.createElement(RadioLabel, {
        "data-hpy-web-radio-field": true,
        ...setErrorDataAttribute(hasError(meta))
    }, React__default.createElement(Inline, null, React__default.createElement(RadioInputWrapper, null, React__default.createElement("input", {
        type: "radio",
        ...props,
        ...setTestId("RadioField-" + testId),
        "data-hpy-web-radio-field-input": true,
        value: value,
        checked: field.checked,
        onChange: onChange
    }), React__default.createElement(CustomRadioInput, {
        "data-hpy-web-radio-field-custom-input": true
    })), hasLabel && React__default.createElement(LabelWrapper, null, React__default.createElement(Stack, {
        gap: 0.25,
        align: "left"
    }, label && React__default.createElement(Text, {
        size: "medium",
        weight: "bold"
    }, label), sublabel && React__default.createElement(Text, {
        size: "small"
    }, sublabel)))));
};
RadioField.dataId = "data-hpy-web-radio-field";
RadioField.displayName = "RadioField";

export { RadioField };
